import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { Payment, StripeCard } from '../../../clients/quipli-service/payment/payment';
import { Spinner } from '../../../components/spinner/spinner';
import useLocalStorage from 'use-local-storage';
import { useIntervalWithTimeout } from '../../../hooks/use-interval-with-timeout';
import { toast } from '../../../util/toasts';
import { Stripe } from 'stripe';
import Dinero, { Currency } from 'dinero.js';
import { TenantContext } from '../../../context/tenant-context';
import { usePaymentApiV1 } from '../../../clients/quipli-service/payment/payment-api-v1';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { User } from '../../../clients/quipli-service/user/user';
import { DateTime } from 'luxon';
import { safeRound } from '../../../util/util';

interface SetupProps {
  setCardId?: (cardId: number) => void;
  setCard?: (stripeCard: StripeCard) => void;
}

interface PayProps {
  amount?: Dinero.Dinero;
  paymentMethod: PaymentMethodRequestV2;
}

interface RefundProps {
  paymentId?: string;
  amount?: Dinero.Dinero;
}

export const TerminalFacilitator = ({
  locationId,
  action,
  setCardId,
  setCard,
  onCancel,
  onComplete,
  setPayments,
  close,
  payment,
  setupIntent,
  user,
}: {
  locationId: number;
  action?: 'setup' | 'pay' | 'refund';
  onCancel: (card?: StripeCard) => void;
  onComplete?: (payments?: Payment[]) => void;
  setPayments?: (payments: Payment[]) => void;
  close: boolean;
  payment?: Payment;
  setupIntent?: string;
  user: User;
} & SetupProps &
  PayProps &
  RefundProps) => {
  const { cancelTerminalAction, getReaderStatus } = usePaymentApiV2();
  const { getCardsForUser } = usePaymentApiV1();
  const { searchPayments } = useInvoiceApiV1();
  const { configuration } = useContext(TenantContext);
  const [paymentDetail] = useState<Payment | undefined>(payment);
  const [selectedTerminal] = useLocalStorage<string>(`stripeTerminal-${locationId}`, '');
  const [preFeeAmount] = useState<Dinero.Dinero | undefined>(
    payment?.amount
      ? Dinero({
          amount: safeRound(payment.amount * 10000),
          precision: 4,
          currency: configuration!.currency as Currency,
        })
      : undefined,
  );
  const [readerStatus, setReaderStatus] = useState<Stripe.Terminal.Reader>();
  const [timerRunning, setTimerRunning] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (error) toast(error, 'error');
  }, [error]);

  useEffect(() => {
    if (user && !initialized.current) {
      initialized.current = true;
      initializePoll();
    }
  }, [action, user]);

  async function initializePoll() {
    const reader = await getReaderStatus(selectedTerminal);
    setReaderStatus(reader);
  }

  const handleCancel = async () => {
    setTimerRunning(false);
    try {
      await cancelTerminalAction(selectedTerminal);
    } catch (e: any) {
      toast('Error communicating with reader, please restart and check network connectivity', 'error');
    }
    onCancel && onCancel();
  };

  useEffect(() => {
    if (close) {
      handleCancel();
    }
  }, [close]);

  async function handlePaymentComplete() {
    if (paymentDetail && user && preFeeAmount) {
      try {
        const payments = await searchPayments({ userIds: [user.id], ids: [paymentDetail.id] });
        if (payments.items.length > 0) {
          const payment = payments.items.find((p) => p.id == paymentDetail.id)!;
          if (payment.status && payment.status == 'pending' && payment.gatewayStatus == 'requires_payment_method') {
            // haven't gotten webhook completion yet
            return;
          }
          if (payment.status && ['succeeded', 'pending'].includes(payment.status)) {
            toast(`Payment ${payment.status}`);
            setPayments && setPayments([payment]);
            onComplete && onComplete([payment]);
          } else {
            toast('Payment failed, please try again', 'error');
            await handleCancel();
          }
        } else {
          return;
        }
      } catch (e: any) {
        toast(`Could not complete payment: ${e.message}`, 'error');
        await handleCancel();
      }
    }
  }

  async function handleSetupComplete() {
    if (action === 'setup') {
      try {
        const stripeCards = await getCardsForUser(user.id, true, setupIntent);
        const stripeCard = stripeCards.find((c) => c.setupIntentId === setupIntent);
        if (stripeCard) {
          if (stripeCard.last4 == '') {
            return;
          }
          setCardId && setCardId(stripeCard.id);
          setCard && setCard(stripeCard);
          toast('Card successfully added!');
        } else {
          toast('Failed to add card');
        }
        onComplete && onComplete();
      } catch (err: any) {
        setError(err);
        await handleCancel();
      }
    } else {
      handleCancel();
    }
  }

  async function handleRefundComplete() {
    if (user) {
      const payments = await searchPayments({ userIds: [user.id] });
      if (
        payments.items.filter((_) => _.paidAt > DateTime.now().minus({ second: 30 })).some((_) => !!_.refundedPaymentId)
      ) {
        onComplete && onComplete(payments.items);
      }
    }
  }

  async function statusTransition() {
    console.log('Processing new status');
    if (readerStatus?.action?.status === 'succeeded') {
      if (paymentDetail) {
        console.log('Successful payment');
        return await handlePaymentComplete();
      } else if (setupIntent) {
        console.log('Successful card capture');
        return await handleSetupComplete();
      } else {
        console.log('Successful refund');
        return await handleRefundComplete();
      }
    } else if (readerStatus?.action?.status === 'failed') {
      console.log('Failed action');
      toast('Payment failed, please try again', 'error');
      await handleCancel().then(() => {});
      return;
    }
    return;
  }

  useEffect(() => {
    statusTransition();
  }, [readerStatus]);

  useIntervalWithTimeout(
    async () => {
      if (readerStatus?.action?.status === 'in_progress') {
        try {
          const reader = await getReaderStatus(selectedTerminal);
          if (reader?.action?.status === 'succeeded') {
            if (action === 'pay') {
              handlePaymentComplete();
            }
            if (action === 'setup') {
              handleSetupComplete();
            }
            if (action === 'refund') {
              handleRefundComplete();
            }
          }
          return;
        } catch (e: any) {
          toast('Error communicating with reader, please restart and check network connectivity', 'error');
          handleCancel().then(() => {});
        }
      }
    },
    3000,
    timerRunning,
    180000,
    () => {
      if (readerStatus?.action?.status !== 'succeeded') {
        toast('Reader session has timed out, please try again!', 'error');
      }
      handleCancel().then(() => {});
    },
  );

  useEffect(() => {
    const cancelPayment = async () => {
      return await cancelTerminalAction(selectedTerminal);
    };
    window.addEventListener('unload', cancelPayment);
    return () => {
      window.removeEventListener('unload', cancelPayment);
    };
  }, []);

  const getFeeAmount = () => {
    if (!preFeeAmount) return;
    return Dinero({
      amount: safeRound((paymentDetail?.amount ?? 0) * 10000),
      currency: configuration!.currency as Currency,
      precision: 4,
    }).subtract(preFeeAmount);
  };

  const PaymentCapture = () => {
    return (
      <div>
        {paymentDetail && (
          <div>
            <p>
              {user.name} will now be presented with an amount of:{' '}
              {Dinero({
                amount: safeRound(paymentDetail.amount * 10000),
                currency: configuration!.currency as Currency,
                precision: 4,
              }).toFormat()}{' '}
              {(getFeeAmount()?.toUnit() ?? 0) > 0 && `which includes a ${getFeeAmount()!.toFormat()} processing fee`}
            </p>
            <div>
              <button onClick={() => handleCancel()}>Cancel</button>
            </div>
          </div>
        )}
        {!paymentDetail && <Spinner />}
      </div>
    );
  };

  const SetupCapture = () => {
    return (
      <>
        <div
          className="dialog_content"
          style={{ textAlign: 'left', maxWidth: '500px', maxHeight: '600px', overflow: 'auto' }}
          data-animation="pop"
        >
          <div className="mb-0">
            <div className="modal-body">
              <p className="text-center">
                Please <b>swipe, insert,</b> or <b>tap</b> your card now to add a payment method.
              </p>
              <img
                height="350px"
                width="450px"
                src="https://cdn.dribbble.com/users/2874108/screenshots/14050299/media/97c711d0ab40503970efb670a0fb778f.gif"
              />
            </div>
          </div>
        </div>
        <div className="dialog-actions flex justify-start gap-2">
          <button className="button button-small button-block button-secondary" onClick={() => handleCancel()}>
            Cancel
          </button>
        </div>
      </>
    );
  };

  const RefundCapture = () => {
    return (
      <div>
        <div>
          <p>
            {user.name} will be refunded an amount of:{' '}
            {Dinero({
              amount: safeRound(50 * 10000),
              currency: configuration!.currency as Currency,
              precision: 4,
            }).toFormat()}{' '}
          </p>
          <div>
            <button onClick={() => handleCancel()}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  return action == 'pay' ? <PaymentCapture /> : action == 'setup' ? <SetupCapture /> : <RefundCapture />;
};
